/**
 * <keystone-form> — schema-driven form composite (Phase 4.4d.iv v1).
 *
 * Usage:
 *
 *   <keystone-form
 *     schema-url="/api/schemas/supplier"
 *     submit-url="/suppliers"
 *     submit-label="Create supplier">
 *   </keystone-form>
 *
 * Flow:
 *   1. On connect, GET the schema URL.
 *   2. For each property in `schema.properties`, render a
 *      <keystone-field-*> based on the property's `type` (+ `enum`,
 *      `format`).
 *   3. Track field values internally (string-typed) via the
 *      `keystone-field-input` event bubbling up from children.
 *   4. On submit, coerce values to the schema's types, POST to
 *      `submit-url`, render server-side validation errors back into
 *      the matching field widgets.
 *
 * Events:
 *   - `keystone-form-loaded` ({ schema, version })
 *   - `keystone-form-submitted` ({ response })
 *   - `keystone-form-error` ({ errors })
 *
 * Scope of v1:
 *   - Types: string, number, integer, boolean, enum.
 *   - Defers: date, money, relationship picker — Phase 4.4d.v.
 *   - No client-side validation beyond HTML5 native (`required`,
 *     `pattern`, `min`/`max`). The server is the authoritative
 *     validator; we just route its errors back to fields.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";

import "./fields/keystone-field-text.js";
import "./fields/keystone-field-number.js";
import "./fields/keystone-field-enum.js";
import "./fields/keystone-field-boolean.js";
import "./fields/keystone-field-date.js";
import "./fields/keystone-field-money.js";
import "./fields/keystone-field-relationship.js";

interface JsonSchemaProperty {
  type?: string | string[];
  format?: string;
  enum?: string[];
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  minimum?: number;
  maximum?: number;
  title?: string;
  description?: string;
  /**
   * Custom Keystone extensions. The AI configurator (Phase 4.6) will
   * write these; widgets here interpret them.
   */
  "x-references"?: string;
  "x-display-field"?: string;
  "x-format"?: "money" | string;
  "x-currency"?: string;
}

/**
 * Map ISO-4217 currency codes to display symbols. Conservative — the
 * widget falls back to the code itself if we don't have a mapping.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  NZD: "$",
  AUD: "$",
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
};

function currencySymbol(code: string | undefined): string {
  if (!code) return "$";
  return CURRENCY_SYMBOLS[code] ?? code;
}

interface JsonSchema {
  type: "object";
  title?: string;
  properties: Record<string, JsonSchemaProperty>;
  required?: string[];
}

interface SchemaResponse {
  entity_type: string;
  version: number;
  schema: JsonSchema;
}

type FormStatus = "loading" | "ready" | "submitting" | "submitted" | "fatal";

@customElement("keystone-form")
export class KeystoneForm extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family:
        system-ui,
        -apple-system,
        "Segoe UI",
        Roboto,
        sans-serif;
      color: #1a1a1a;
    }

    .form-error,
    .form-success {
      padding: 0.75rem 1rem;
      border-radius: 0.375rem;
      margin: 0.5rem 0 1rem;
      font-size: 0.875rem;
    }

    .form-error {
      background: #fef2f2; /* tw red-50 */
      border: 1px solid #fecaca; /* tw red-200 */
      color: #991b1b; /* tw red-800 */
    }

    .form-success {
      background: #f0fdf4; /* tw green-50 */
      border: 1px solid #bbf7d0; /* tw green-200 */
      color: #166534; /* tw green-800 */
    }

    .actions {
      margin-top: 1rem;
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }

    button[type="submit"] {
      background: #2a5a8c;
      color: white;
      border: 0;
      padding: 0.5rem 1.25rem;
      border-radius: 0.375rem;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
    }

    button[type="submit"]:hover {
      background: #1a4d8c;
    }

    button[type="submit"]:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .loading,
    .meta {
      color: #6b7280;
      font-size: 0.875rem;
    }
  `;

  @property({ attribute: "schema-url", type: String })
  schemaUrl = "";

  @property({ attribute: "submit-url", type: String })
  submitUrl = "";

  @property({ attribute: "submit-method", type: String })
  submitMethod: "POST" | "PUT" = "POST";

  @property({ attribute: "submit-label", type: String })
  submitLabel = "Save";

  /**
   * Optional URL to fetch existing data from for hydration. Used by
   * edit pages — the form GETs this after the schema, expects a
   * response shaped like `{ <root>: { <field>: ... } }` where <root>
   * matches `seed-root` (default: the schema's `title` lower-cased,
   * or the entity_type from the schema response).
   *
   * Why a root key: the existing /suppliers/:id endpoint returns
   * `{ supplier: { name, contactEmail, ... } }`, not the raw payload.
   * The root attr lets pages stay backwards-compatible.
   */
  @property({ attribute: "seed-url", type: String })
  seedUrl = "";

  @property({ attribute: "seed-root", type: String })
  seedRoot = "";

  @state() private status: FormStatus = "loading";
  @state() private schema: JsonSchema | null = null;
  @state() private schemaVersion: number | null = null;
  @state() private values: Record<string, string> = {};
  @state() private fieldErrors: Record<string, string> = {};
  @state() private formError: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadSchema();
    this.addEventListener("keystone-field-input", this.onFieldInput as EventListener);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener(
      "keystone-field-input",
      this.onFieldInput as EventListener,
    );
  }

  private onFieldInput = (e: CustomEvent<{ name: string; value: string }>): void => {
    const { name, value } = e.detail;
    this.values = { ...this.values, [name]: value };
    // Clear the field-level error as soon as the user edits.
    if (this.fieldErrors[name]) {
      const { [name]: _drop, ...rest } = this.fieldErrors;
      this.fieldErrors = rest;
    }
  };

  private async loadSchema(): Promise<void> {
    if (!this.schemaUrl) {
      this.status = "fatal";
      this.formError = "No schema-url provided.";
      return;
    }

    try {
      const res = await fetch(this.schemaUrl, {
        headers: { accept: "application/json" },
      });
      if (!res.ok) {
        throw new Error(`Schema fetch failed: HTTP ${res.status}`);
      }
      const body = (await res.json()) as SchemaResponse;
      this.schema = body.schema;
      this.schemaVersion = body.version;
      // Seed empty string values for every property so input bindings
      // stay reactive.
      const seed: Record<string, string> = {};
      for (const key of Object.keys(body.schema.properties)) {
        seed[key] = "";
      }
      this.values = seed;

      if (this.seedUrl) {
        await this.loadSeed(body.entity_type);
      }

      this.status = "ready";

      this.dispatchEvent(
        new CustomEvent("keystone-form-loaded", {
          detail: { schema: this.schema, version: this.schemaVersion },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      this.status = "fatal";
      this.formError =
        err instanceof Error ? err.message : "Failed to load schema.";
    }
  }

  /**
   * Hydrate `values` from `seed-url`. Called after the schema is loaded
   * so we know which keys to look for. A missing key or a fetch error
   * is non-fatal — the form is still usable, just not pre-seeded.
   *
   * Wire-shape conventions for existing endpoints (suppliers, PO):
   *   - Response is wrapped: { supplier: {...} } / { purchaseOrder: {...} }
   *   - Field names are camelCase on the wire (contactEmail), but our
   *     schema uses snake_case (contact_email). We try both spellings.
   */
  private async loadSeed(entityType: string): Promise<void> {
    if (!this.schema) return;
    try {
      const res = await fetch(this.seedUrl, {
        headers: { accept: "application/json" },
      });
      if (!res.ok) return;
      const body = (await res.json()) as Record<string, unknown>;
      const root =
        this.seedRoot ||
        // Convention: `entity_type` (snake) → `entityType` (camel) for the root.
        entityType.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      const data =
        (body[root] as Record<string, unknown> | undefined) ?? body;

      const next: Record<string, string> = { ...this.values };
      for (const key of Object.keys(this.schema.properties)) {
        const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        const raw = data[key] ?? data[camelKey];
        if (raw === undefined || raw === null) continue;
        next[key] = typeof raw === "boolean" ? (raw ? "true" : "") : String(raw);
      }
      this.values = next;
    } catch {
      // Non-fatal: form stays usable without seeding.
    }
  }

  /**
   * Coerce string-typed form values into the JSON Schema types so the
   * server gets a payload that matches the schema. Empty strings for
   * optional fields are dropped from the payload entirely so the
   * server's `additionalProperties: false` schemas don't reject them.
   */
  private buildPayload(): Record<string, unknown> {
    if (!this.schema) return {};
    const required = new Set(this.schema.required ?? []);
    const payload: Record<string, unknown> = {};
    for (const [name, prop] of Object.entries(this.schema.properties)) {
      const raw = this.values[name] ?? "";
      const isRequired = required.has(name);
      const type = Array.isArray(prop.type) ? prop.type[0] : prop.type;

      if (raw === "" && !isRequired) {
        continue; // drop optional empties
      }

      switch (type) {
        case "integer":
          payload[name] = raw === "" ? null : parseInt(raw, 10);
          break;
        case "number":
          payload[name] = raw === "" ? null : Number(raw);
          break;
        case "boolean":
          payload[name] = raw === "true";
          break;
        default: // string + enum
          payload[name] = raw;
      }
    }
    return payload;
  }

  /**
   * Parse the AJV-style error messages our server returns. The wire
   * shape is { error: "validation_error", issues: ["...path msg", ...] }.
   * Each issue looks like "/name must NOT be shorter than 1 characters"
   * or "(root) must have required property 'name'".
   *
   * Strategy: look for the first JSON Pointer-ish path token and use
   * its last segment as the field name. Anything we can't pin to a
   * field becomes the form-level error.
   */
  private routeIssues(issues: string[]): void {
    const fieldErrors: Record<string, string> = {};
    const formIssues: string[] = [];

    for (const issue of issues) {
      // Pattern A: "/path message"
      const pathMatch = issue.match(/^\/([\w./]+)\s+(.+)$/);
      // Pattern B: "(root) must have required property 'name'"
      const requiredMatch = issue.match(/required property\s+['"]([\w_]+)['"]/);

      if (pathMatch) {
        const segments = pathMatch[1].split("/");
        const field = segments[segments.length - 1];
        const msg = pathMatch[2];
        fieldErrors[field] = msg;
      } else if (requiredMatch) {
        const field = requiredMatch[1];
        fieldErrors[field] = "This field is required.";
      } else {
        formIssues.push(issue);
      }
    }

    this.fieldErrors = fieldErrors;
    this.formError = formIssues.length ? formIssues.join(" · ") : null;
  }

  private async onSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (!this.schema || !this.submitUrl) return;

    this.status = "submitting";
    this.formError = null;
    this.fieldErrors = {};

    const payload = this.buildPayload();

    try {
      const res = await fetch(this.submitUrl, {
        method: this.submitMethod,
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        issues?: string[];
      } & Record<string, unknown>;

      if (res.ok) {
        this.status = "submitted";
        this.dispatchEvent(
          new CustomEvent("keystone-form-submitted", {
            detail: { response: body },
            bubbles: true,
            composed: true,
          }),
        );
        return;
      }

      if (res.status === 400 && Array.isArray(body.issues)) {
        this.routeIssues(body.issues);
      } else {
        this.formError =
          (typeof body.error === "string" ? body.error : null) ??
          `Request failed: HTTP ${res.status}`;
      }
      this.status = "ready";
      this.dispatchEvent(
        new CustomEvent("keystone-form-error", {
          detail: { status: res.status, body },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      this.status = "ready";
      this.formError =
        err instanceof Error ? err.message : "Submit failed.";
    }
  }

  private renderField(name: string, prop: JsonSchemaProperty) {
    const required = (this.schema?.required ?? []).includes(name);
    const value = this.values[name] ?? "";
    const error = this.fieldErrors[name] ?? null;
    const label = prop.title ?? "";

    const type = Array.isArray(prop.type) ? prop.type[0] : prop.type;

    // Order of precedence for type dispatch:
    //   1. x-references     → relationship (overrides any type)
    //   2. x-format=money   → money       (overrides integer)
    //   3. enum             → enum         (existing)
    //   4. format=date      → date         (string + format)
    //   5. type switch      → string/number/integer/boolean fallback

    if (prop["x-references"]) {
      return html`<keystone-field-relationship
        name=${name}
        label=${label}
        value=${value}
        ?required=${required}
        error=${ifDefined(error ?? undefined)}
        target=${prop["x-references"]}
        display-field=${prop["x-display-field"] ?? "name"}
      ></keystone-field-relationship>`;
    }

    if (prop["x-format"] === "money") {
      return html`<keystone-field-money
        name=${name}
        label=${label}
        value=${value}
        ?required=${required}
        error=${ifDefined(error ?? undefined)}
        currency-symbol=${currencySymbol(prop["x-currency"])}
      ></keystone-field-money>`;
    }

    if (Array.isArray(prop.enum) && prop.enum.length > 0) {
      return html`<keystone-field-enum
        name=${name}
        label=${label}
        value=${value}
        ?required=${required}
        error=${ifDefined(error ?? undefined)}
        .options=${prop.enum}
      ></keystone-field-enum>`;
    }

    if (type === "string" && prop.format === "date") {
      return html`<keystone-field-date
        name=${name}
        label=${label}
        value=${value}
        ?required=${required}
        error=${ifDefined(error ?? undefined)}
      ></keystone-field-date>`;
    }

    switch (type) {
      case "number":
        return html`<keystone-field-number
          name=${name}
          label=${label}
          value=${value}
          ?required=${required}
          error=${ifDefined(error ?? undefined)}
          min=${ifDefined(prop.minimum ?? undefined)}
          max=${ifDefined(prop.maximum ?? undefined)}
        ></keystone-field-number>`;
      case "integer":
        return html`<keystone-field-number
          name=${name}
          label=${label}
          value=${value}
          ?required=${required}
          error=${ifDefined(error ?? undefined)}
          ?integer=${true}
          min=${ifDefined(prop.minimum ?? undefined)}
          max=${ifDefined(prop.maximum ?? undefined)}
        ></keystone-field-number>`;
      case "boolean":
        return html`<keystone-field-boolean
          name=${name}
          label=${label}
          value=${value}
          ?required=${required}
          error=${ifDefined(error ?? undefined)}
        ></keystone-field-boolean>`;
      case "string":
      default:
        return html`<keystone-field-text
          name=${name}
          label=${label}
          value=${value}
          ?required=${required}
          error=${ifDefined(error ?? undefined)}
          format=${ifDefined(prop.format || undefined)}
          minlength=${ifDefined(prop.minLength ?? undefined)}
          maxlength=${ifDefined(prop.maxLength ?? undefined)}
          pattern=${ifDefined(prop.pattern || undefined)}
        ></keystone-field-text>`;
    }
  }

  override render() {
    if (this.status === "loading") {
      return html`<p class="loading">Loading form…</p>`;
    }

    if (this.status === "fatal") {
      return html`<div class="form-error" role="alert">
        ${this.formError ?? "Form failed to load."}
      </div>`;
    }

    if (this.status === "submitted") {
      return html`<div class="form-success" role="status">
        Saved. ✅
      </div>`;
    }

    if (!this.schema) {
      // Defensive: shouldn't hit this with the status guards above.
      return html``;
    }

    return html`
      <form @submit=${this.onSubmit} novalidate>
        ${this.formError
          ? html`<div class="form-error" role="alert">${this.formError}</div>`
          : ""}
        ${Object.entries(this.schema.properties).map(([name, prop]) =>
          this.renderField(name, prop),
        )}
        <div class="actions">
          <button type="submit" ?disabled=${this.status === "submitting"}>
            ${this.status === "submitting" ? "Saving…" : this.submitLabel}
          </button>
          <span class="meta"
            >Schema v${this.schemaVersion ?? "?"}</span
          >
        </div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-form": KeystoneForm;
  }
}
